import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

const LazyCell = ({
  queryKey,
  queryName,
  additionalQueriesResults,
  rowId,
  renderContent,
  loadingComponent: LoadingComponent,
  errorComponent: ErrorComponent,
  showPartialResult,
  matchNodeId,
}) => {
  const queryResult = useMemo(
    () => additionalQueriesResults?.[queryKey],
    [additionalQueriesResults, queryKey],
  );

  const resolveNodeId = matchNodeId || ((node) => node?.id);
  const { loading, error, data } = queryResult || {};

  if (loading && !showPartialResult) {
    return LoadingComponent ? <LoadingComponent /> : <CircularProgress size={20} />;
  }

  if (data) {
    const edges = data?.[queryName]?.edges || [];
    const matchingEdge = edges.find((edge) => resolveNodeId(edge.node) === rowId);
    return renderContent(matchingEdge);
  }

  if (loading && showPartialResult) {
    return renderContent(data);
  }

  if (error) {
    return ErrorComponent ? (
      <ErrorComponent error={error} />
    ) : (
      <Typography color="error" variant="caption">
        Error loading data
      </Typography>
    );
  }

  return null;
};

LazyCell.propTypes = {
  queryKey: PropTypes.string.isRequired,
  queryName: PropTypes.string.isRequired,
  additionalQueriesResults: PropTypes.shape({}),
  rowId: PropTypes.string.isRequired,
  renderContent: PropTypes.func.isRequired,
  loadingComponent: PropTypes.elementType,
  errorComponent: PropTypes.elementType,
  showPartialResult: PropTypes.bool,
  matchNodeId: PropTypes.func,
};

LazyCell.defaultProps = {
  additionalQueriesResults: {},
  loadingComponent: null,
  errorComponent: null,
  showPartialResult: false,
  matchNodeId: null,
};

export default LazyCell;
